// ══════════════════════════════════════════════════════════════════════════════
// PAGE GROUPES / FAMILLE - ABRACADATCHAT
// Liste des groupes, création, invitation
// ══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, Plus, Link2, Copy, Check, Settings, MessageSquare,
  Image, Calendar, ChevronRight, Loader2, X, Sparkles,
  UserPlus, Share2, QrCode, ArrowLeft
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Limite max de groupes/feeds pour les Particuliers (gratuit)
const MAX_GROUPS_PARTICULIERS = 5;

// Types de groupes avec emojis
const GROUP_TYPES = [
  { id: 'famille', name: 'Famille', emoji: '👨‍👩‍👧‍👦', color: 'from-rose-500 to-pink-500' },
  { id: 'amis', name: 'Amis', emoji: '🎉', color: 'from-amber-500 to-orange-500' },
  { id: 'travail', name: 'Travail', emoji: '💼', color: 'from-blue-500 to-indigo-500' },
  { id: 'voisins', name: 'Voisins', emoji: '🏠', color: 'from-green-500 to-emerald-500' },
  { id: 'sport', name: 'Sport', emoji: '⚽', color: 'from-cyan-500 to-teal-500' },
  { id: 'loisirs', name: 'Loisirs', emoji: '🎮', color: 'from-purple-500 to-violet-500' },
  { id: 'autre', name: 'Autre', emoji: '👥', color: 'from-slate-500 to-gray-500' },
];

export default function GroupesPage() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(null); // group object
  const [copiedLink, setCopiedLink] = useState(false);
  
  // Détection mobile et clavier
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  useEffect(() => {
    if (!showCreateModal || !isMobile) {
      setKeyboardHeight(0);
      return;
    }
    
    // Masquer la navbar quand le modal est ouvert
    document.body.classList.add('modal-fullscreen-open');
    
    const viewport = window.visualViewport;
    if (!viewport) return;
    
    const handleViewportResize = () => {
      const heightDiff = window.innerHeight - viewport.height;
      setKeyboardHeight(heightDiff > 50 ? heightDiff : 0);
    };
    
    viewport.addEventListener('resize', handleViewportResize);
    viewport.addEventListener('scroll', handleViewportResize);
    
    return () => {
      document.body.classList.remove('modal-fullscreen-open');
      viewport.removeEventListener('resize', handleViewportResize);
      viewport.removeEventListener('scroll', handleViewportResize);
    };
  }, [showCreateModal, isMobile]);
  
  // Form state
  const [newGroup, setNewGroup] = useState({
    name: '',
    description: '',
    type: 'famille',
    emoji: '👨‍👩‍👧‍👦'
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    setLoading(true);
    const token = localStorage.getItem('buyer_token') || localStorage.getItem('client_token');
    
    try {
      const response = await fetch(`${API_URL}/api/groups/my-groups`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setGroups(data.groups || []);
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
    } finally {
      setLoading(false);
    }
  };

  const createGroup = async () => {
    // Vérifier la limite de groupes
    if (groups.length >= MAX_GROUPS_PARTICULIERS) {
      toast.error(`Tu as atteint la limite de ${MAX_GROUPS_PARTICULIERS} salons. Supprime un salon existant pour en créer un nouveau.`);
      return;
    }
    
    if (!newGroup.name.trim()) {
      toast.error('Donne un nom à ton groupe !');
      return;
    }
    
    setCreating(true);
    const token = localStorage.getItem('buyer_token') || localStorage.getItem('client_token');
    
    try {
      const response = await fetch(`${API_URL}/api/groups/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newGroup)
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setShowCreateModal(false);
        setNewGroup({ name: '', description: '', type: 'famille', emoji: '👨‍👩‍👧‍👦' });
        fetchGroups();
        
        // Afficher le modal d'invitation
        setShowInviteModal({
          name: newGroup.name,
          invite_link: data.invite_link,
          invite_code: data.invite_code
        });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la création');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setCreating(false);
    }
  };

  const copyInviteLink = (link) => {
    navigator.clipboard.writeText(link);
    setCopiedLink(true);
    toast.success('Lien copié !');
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const shareInviteLink = async (group) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Rejoins ${group.name} sur Abracadatchat`,
          text: `Tu es invité(e) à rejoindre le groupe "${group.name}"`,
          url: group.invite_link
        });
      } catch (error) {
        copyInviteLink(group.invite_link);
      }
    } else {
      copyInviteLink(group.invite_link);
    }
  };

  const getTypeColor = (type) => {
    const found = GROUP_TYPES.find(t => t.id === type);
    return found?.color || 'from-slate-500 to-gray-500';
  };

  // ════════════════════════════════════════════════════════════════
  // COMPOSANT CARD GROUPE
  // ════════════════════════════════════════════════════════════════
  
  const GroupCard = ({ group }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-lg transition-all group cursor-pointer"
      onClick={() => navigate(`/cockpit-particulier/groupes/${group.id}`)}
      data-testid={`group-card-${group.id}`}
    >
      {/* Header avec gradient */}
      <div className={`h-24 bg-gradient-to-r ${getTypeColor(group.type)} relative`}>
        <div className="absolute inset-0 bg-black/10" />
        <div className="absolute bottom-4 left-4 flex items-center gap-3">
          <span className="text-4xl">{group.emoji}</span>
          <div>
            <h3 className="text-xl font-bold text-white">{group.name}</h3>
            <p className="text-white/80 text-sm">{group.member_count} membre{group.member_count > 1 ? 's' : ''}</p>
          </div>
        </div>
        {group.is_admin && (
          <span className="absolute top-3 right-3 bg-white/20 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full">
            Admin
          </span>
        )}
      </div>
      
      {/* Stats */}
      <div className="p-4">
        <div className="flex items-center justify-between text-sm text-slate-500 mb-4">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <MessageSquare size={14} />
              {group.post_count} posts
            </span>
            <span className="flex items-center gap-1">
              <Image size={14} />
              {group.photo_count} photos
            </span>
            <span className="flex items-center gap-1">
              <Calendar size={14} />
              {group.event_count} events
            </span>
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            className="flex-1"
            onClick={(e) => {
              e.stopPropagation();
              setShowInviteModal(group);
            }}
          >
            <UserPlus size={14} className="mr-1" />
            Inviter
          </Button>
          <Button 
            size="sm" 
            className={`flex-1 bg-gradient-to-r ${getTypeColor(group.type)} text-white border-0`}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/cockpit-particulier/groupes/${group.id}`);
            }}
          >
            Ouvrir
            <ChevronRight size={14} className="ml-1" />
          </Button>
        </div>
      </div>
    </motion.div>
  );

  // ════════════════════════════════════════════════════════════════
  // RENDU
  // ════════════════════════════════════════════════════════════════

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6" data-testid="groupes-page">
      {/* Header - Responsive pour Samsung S25 */}
      <div className="flex items-center justify-between gap-2 mb-6 sm:mb-8">
        <div className="flex-1 min-w-0">
          <h1 className="text-xl sm:text-3xl font-bold text-slate-800 whitespace-nowrap">Mes Groupes Privés</h1>
          <p className="text-xs sm:text-sm text-slate-500 truncate">
            Famille, amis, voisins... Reste connecté 
            <span className="ml-1 text-orange-500 font-medium">({groups.length}/{MAX_GROUPS_PARTICULIERS})</span>
          </p>
        </div>
        <Button 
          onClick={() => {
            if (groups.length >= MAX_GROUPS_PARTICULIERS) {
              toast.error(`Limite de ${MAX_GROUPS_PARTICULIERS} salons atteinte`);
              return;
            }
            setShowCreateModal(true);
          }}
          className={`text-xs sm:text-sm px-3 py-2 sm:px-4 whitespace-nowrap flex-shrink-0 ${
            groups.length >= MAX_GROUPS_PARTICULIERS 
              ? 'bg-gray-300 cursor-not-allowed' 
              : 'bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600'
          }`}
          disabled={groups.length >= MAX_GROUPS_PARTICULIERS}
          data-testid="create-group-btn"
        >
          <Plus size={16} className="mr-1 sm:mr-2" />
          <span className="hidden sm:inline">Créer un salon</span>
          <span className="sm:hidden">Nouveau</span>
        </Button>
      </div>

      {/* Liste des groupes */}
      {groups.length === 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl sm:rounded-3xl p-6 sm:p-12 text-center border border-orange-200"
        >
          <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6 bg-gradient-to-r from-orange-500 to-amber-500 rounded-full flex items-center justify-center">
            <Users className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
          </div>
          <h3 className="text-lg sm:text-2xl font-bold text-slate-800 mb-2">Crée ton premier groupe privé !</h3>
          <p className="text-sm sm:text-base text-slate-600 mb-4 sm:mb-6 max-w-md mx-auto">
            Invite ta famille, tes amis ou tes voisins à rejoindre un espace privé pour partager et discuter.
          </p>
          <Button 
            onClick={() => setShowCreateModal(true)}
            className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-sm px-4 py-2"
          >
            <Plus size={16} className="mr-2" />
            Créer mon premier groupe privé
          </Button>
        </motion.div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {groups.map((group) => (
            <GroupCard key={group.id} group={group} />
          ))}
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* MODAL CRÉATION - Version mobile plein écran via Portal */}
      {/* ════════════════════════════════════════════════════════════════ */}
      {showCreateModal && isMobile && createPortal(
        <AnimatePresence>
          <motion.div
            key="create-group-modal"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="fixed inset-0 z-[10002] flex flex-col bg-white"
          >
            {/* Header avec safe area */}
            <header 
              className="flex items-center gap-2 px-3 border-b border-gray-100"
              style={{ paddingTop: 'max(12px, env(safe-area-inset-top))', paddingBottom: '12px' }}
            >
              <button 
                onClick={() => setShowCreateModal(false)}
                className="p-1 text-gray-500"
              >
                <ArrowLeft size={22} />
              </button>
              <h1 className="text-[15px] font-bold text-gray-900">Créer un groupe privé</h1>
            </header>

              {/* Contenu scrollable */}
              <div className="flex-1 overflow-y-auto p-5">
                {/* Type de groupe - Style filtres premium horizontal */}
                <div className="mb-4">
                  <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">
                    Type de groupe
                  </label>
                  <div 
                    className="flex gap-1.5 overflow-x-auto scrollbar-hide pb-2"
                    style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                  >
                    {GROUP_TYPES.map((type) => (
                      <button
                        key={type.id}
                        onClick={() => setNewGroup({ ...newGroup, type: type.id, emoji: type.emoji })}
                        className={`flex-shrink-0 flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all ${
                          newGroup.type === type.id
                            ? 'border-orange-500 bg-orange-50'
                            : 'border-gray-200 bg-white'
                        }`}
                      >
                        <span className="text-xl">{type.emoji}</span>
                        <span className="text-[10px] text-gray-600">{type.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Nom du groupe */}
                <div className="mb-4">
                  <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">
                    Nom du groupe *
                  </label>
                  <input
                    type="text"
                    value={newGroup.name}
                    onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
                    placeholder="Ex: Famille Dupont, Les voisins du 3ème..."
                    className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"
                    data-testid="group-name-input"
                  />
                </div>
                
                {/* Description */}
                <div className="mb-4">
                  <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">
                    Description (optionnel)
                  </label>
                  <textarea
                    value={newGroup.description}
                    onChange={(e) => setNewGroup({ ...newGroup, description: e.target.value })}
                    placeholder="Une courte description de ce groupe..."
                    rows={3}
                    className="w-full p-4 rounded-xl border text-[15px] resize-none bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"
                  />
                </div>
              </div>

              {/* Footer fixe */}
              <div 
                className="p-4 border-t border-gray-100 bg-white transition-all duration-200"
                style={{ 
                  paddingBottom: keyboardHeight > 0 ? '16px' : 'calc(16px + env(safe-area-inset-bottom, 0px))',
                  marginBottom: keyboardHeight > 0 ? `${keyboardHeight}px` : '0px'
                }}
              >
                <button
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 mb-3 text-sm font-medium rounded-xl transition-all text-orange-600 bg-orange-50 hover:bg-orange-100 border border-orange-200"
                >
                  <Sparkles size={16} />
                  Créer avec Abracada
                </button>
                
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 h-12 rounded-xl font-semibold transition-all border border-gray-300 text-[#1e3a5f] hover:bg-gray-100"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={createGroup}
                    disabled={creating || !newGroup.name.trim()}
                    data-testid="confirm-create-group-btn"
                    className="flex-1 h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold shadow-lg shadow-orange-500/25 transition-all disabled:opacity-50"
                  >
                    {creating ? 'Création...' : 'Créer'}
                  </button>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>,
        document.body
      )}

      {/* MODAL CRÉATION - Version desktop */}
      <AnimatePresence>
        {showCreateModal && !isMobile && (
            /* ═══ VERSION DESKTOP ═══ */
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 md:left-[288px] bg-black/50 z-[10002] flex items-center justify-center p-4"
              onClick={() => setShowCreateModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9, y: 50 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 50 }}
                className="bg-white rounded-2xl w-full max-w-lg overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="bg-gradient-to-r from-orange-500 to-amber-500 p-6 text-white">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-bold">Créer un groupe privé</h2>
                    <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-white/20 rounded-full">
                      <X size={20} />
                    </button>
                  </div>
                </div>
                
                {/* Form */}
                <div className="p-6 space-y-6">
                  {/* Type de groupe */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-3">
                      Type de groupe
                    </label>
                    <div className="grid grid-cols-4 gap-2">
                      {GROUP_TYPES.map((type) => (
                        <button
                          key={type.id}
                          onClick={() => setNewGroup({ ...newGroup, type: type.id, emoji: type.emoji })}
                          className={`p-3 rounded-xl border-2 transition-all ${
                            newGroup.type === type.id
                              ? 'border-orange-500 bg-orange-50'
                              : 'border-slate-200 hover:border-orange-300'
                          }`}
                        >
                          <span className="text-2xl block mb-1">{type.emoji}</span>
                          <span className="text-xs text-slate-600">{type.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Nom du groupe */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Nom du groupe *
                    </label>
                    <input
                      type="text"
                      value={newGroup.name}
                      onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
                      placeholder="Ex: Famille Dupont, Les voisins du 3ème..."
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      data-testid="group-name-input"
                    />
                  </div>
                  
                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Description (optionnel)
                    </label>
                    <textarea
                      value={newGroup.description}
                      onChange={(e) => setNewGroup({ ...newGroup, description: e.target.value })}
                      placeholder="Une courte description de ce groupe..."
                      rows={2}
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none"
                    />
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-3 pt-4">
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => setShowCreateModal(false)}
                    >
                      Annuler
                    </Button>
                    <Button 
                      className="flex-1 bg-gradient-to-r from-orange-500 to-amber-500"
                      onClick={createGroup}
                      disabled={creating || !newGroup.name.trim()}
                      data-testid="confirm-create-group-btn"
                    >
                      {creating ? (
                        <>
                          <Loader2 size={18} className="mr-2 animate-spin" />
                          Création...
                        </>
                      ) : (
                        <>
                          <Sparkles size={18} className="mr-2" />
                          Créer
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
        )}
      </AnimatePresence>

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* MODAL INVITATION */}
      {/* ════════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {showInviteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 md:left-[288px] bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowInviteModal(null)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 50 }}
              className="bg-white rounded-2xl w-full max-w-md overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-orange-500 to-amber-500 p-6 text-white text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center">
                  <UserPlus size={32} />
                </div>
                <h2 className="text-xl font-bold">Inviter des membres</h2>
                <p className="text-white/80 text-sm mt-1">dans "{showInviteModal.name}"</p>
              </div>
              
              {/* Content */}
              <div className="p-6 space-y-4">
                <p className="text-slate-600 text-center text-sm">
                  Partage ce lien avec les personnes que tu veux inviter. 
                  Elles pourront rejoindre le groupe en un clic !
                </p>
                
                {/* Lien */}
                <div className="bg-slate-100 rounded-xl p-4">
                  <div className="flex items-center gap-2">
                    <Link2 size={18} className="text-slate-400 flex-shrink-0" />
                    <input
                      type="text"
                      value={showInviteModal.invite_link}
                      readOnly
                      className="flex-1 bg-transparent text-sm text-slate-700 outline-none truncate"
                    />
                    <button
                      onClick={() => copyInviteLink(showInviteModal.invite_link)}
                      className={`p-2 rounded-lg transition-colors ${
                        copiedLink ? 'bg-green-500 text-white' : 'bg-orange-500 text-white hover:bg-orange-600'
                      }`}
                    >
                      {copiedLink ? <Check size={18} /> : <Copy size={18} />}
                    </button>
                  </div>
                </div>
                
                {/* Actions */}
                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => setShowInviteModal(null)}
                  >
                    Fermer
                  </Button>
                  <Button 
                    className="flex-1 bg-gradient-to-r from-orange-500 to-amber-500"
                    onClick={() => shareInviteLink(showInviteModal)}
                  >
                    <Share2 size={18} className="mr-2" />
                    Partager
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
